import moment from 'moment';
import * as React from 'react';

import { routes } from 'defs';
import { Date as JDate } from 'defs/java/Date';
import i18n from 'helper/i18n';
import noop from 'helper/noop';

import Checkbox from 'components/atoms/Checkbox';
import FieldInput from 'components/atoms/FieldInput';
import Spacer from 'components/helper/Spacer';
import Agreement from 'components/molecules/Agreement';
import ButtonBar from 'components/molecules/ButtonBar';
import { ButtonBarProps } from 'components/molecules/ButtonBar';
import ElementList from 'components/molecules/ElementList';
import leaveCoach from 'components/organisms/Anamnesis/Components/leave-coach';

import { isNativePlatformAndroid, isNativePlatformIOS } from 'components/helper/Platform';
import withValidation from 'components/hoc/withValidation';
import { AnamnesisStepResolver } from 'components/organisms/Anamnesis';
import sanitize from 'helper/sanitizer';
import { VALIDATE_IS_NOT_ALLOWED, VALIDATE_IS_NOT_RECOMMENDED, VALIDATE_IS_PENDING } from 'helper/validator';
import ageValidatorFactory from 'helper/validator/age';

const ControlledInput = withValidation(
	FieldInput,
	ageValidatorFactory,
	[sanitize.replaceCommasWithDots, sanitize.stripChars, sanitize.stripInvalidDateChars, sanitize.maxLength(10)],
	{
		shouldValidateInitial: true,
	}
);

export type DetermineAgeProps = {
	actionButtonsActive: ButtonBarProps;
	actionButtonsInactive: ButtonBarProps;
	inputField: any;
	isUpdatingAnamnesis: boolean;
	value?: JDate;
	onNavigationClick: (targetUrl: { href: string }) => void /* parent */;
	onStepCompleted: (resolver: AnamnesisStepResolver) => void /* parent */;
	doSaveAge: Function /* actionCreator */;
};

export type DetermineAgeState = {
	inputValue: string;
	inputError?: string;
	hasAcceptedWarning: boolean;
};

export default class DetermineAge extends React.Component<DetermineAgeProps, DetermineAgeState> {
	public static displayName = 'AnamnesisDetermineAge';

	public static defaultProps = {
		inputField: {
			delegate: noop,
		},
		actionButtonsActive: {},
		actionButtonsInactive: {},
		doSaveAge: noop,
	};

	constructor(props: DetermineAgeProps) {
		super(props);

		this.state = {
			hasAcceptedWarning: false,
			inputError: undefined,
			inputValue: this.inputValueFromProps(),
		};
	}

	public inputValueFromProps(props?: DetermineAgeProps): string {
		const _props = props ? props : this.props;
		if (_props.value && _props.value.date) {
			const date = _props.value;
			return `${date.dayOfMonth}.${date.monthValue}.${date.year}`;
		}
		return '';
	}

	public componentDidUpdate(prevProps: DetermineAgeProps): any {
		if (!this.props.value || !this.props.value.date) {
			return;
		}

		const nextDate = this.props.value;
		const nextValue = `${nextDate.dayOfMonth}.${nextDate.monthValue}.${nextDate.year}`;
		const value = this.inputValueFromProps(prevProps);

		if (nextValue !== value || !this.state.inputValue) {
			this.setState({
				inputValue: nextValue,
			});
		}
	}

	public isRecommended() {
		return this.state.inputError !== VALIDATE_IS_NOT_RECOMMENDED;
	}

	public isValidSubmission() {
		const { inputValue, inputError, hasAcceptedWarning } = this.state;

		const inacceptableValues = [VALIDATE_IS_NOT_ALLOWED, VALIDATE_IS_PENDING];

		if (inputValue === '') {
			return false;
		}

		if (inacceptableValues.includes(inputError!)) {
			return false;
		}

		if (inputError === VALIDATE_IS_NOT_RECOMMENDED && !hasAcceptedWarning) {
			return false;
		}

		return true;
	}

	public handleInputChange = (value: number | string) => {
		this.setState({
			inputValue: typeof value === 'number' ? value.toString() : value || '',
			inputError: VALIDATE_IS_PENDING,
		});
	};

	public handleInputValidation = (error?: string) => {
		this.setState({
			inputError: error,
		});
	};

	public handleButtonClickComplete = () => {
		const _this = this;
		const { onStepCompleted } = this.props;

		onStepCompleted.apply(null, [
			{
				waitFor: 'savedAge',
				actionCreator: _this.props.doSaveAge,
				nextRoute: routes.anamnesis.determineHeight,
				data: {
					birthdate: moment(_this.state.inputValue, 'DD.MM.YYYY').format('YYYY-MM-DD'),
					acceptedAgeWarning: _this.state.hasAcceptedWarning,
				},
			},
		]);
	};

	public onHandleButtonClicks = (type: string) => {
		const { onNavigationClick, onStepCompleted } = this.props;

		if (type === 'completed' && typeof onStepCompleted === 'function') {
			this.handleButtonClickComplete();
			return;
		}

		if (typeof onNavigationClick === 'function') {
			type = type === 'completed' ? 'next' : type;
			onNavigationClick.apply(this, [routes.anamnesis.selectGender]);
		}
	};

	public onChangeAcceptHint = (accepted: boolean) => {
		this.setState({
			hasAcceptedWarning: !!accepted,
		});
	};

	get enableMobileDateKeyboard(): boolean {
		return isNativePlatformIOS();
	}

	public renderWarning() {
		return (
			<Agreement text={i18n('react.impulsCoach.components.anamnesis.determineAge.validation.interference.text')}>
				<ElementList>
					<Checkbox
						tag="li"
						id="anamnesis-accept-warning"
						name="anamnesis-accept-warning"
						label={i18n(
							'react.impulsCoach.components.anamnesis.determineAge.validation.interference.acceptText'
						)}
						onChange={this.onChangeAcceptHint}
						modifier="white"
					/>
				</ElementList>
			</Agreement>
		);
	}

	public renderActiveButtons() {
		return (
			<div className="o-anamnesis__button-bar">
				<ButtonBar
					{...this.props.actionButtonsActive}
					loading={this.props.isUpdatingAnamnesis}
					handleButtonClicks={this.onHandleButtonClicks}
				/>
			</div>
		);
	}

	public renderInactiveButtons() {
		return (
			<div className="o-anamnesis__button-bar">
				<ButtonBar
					{...this.props.actionButtonsInactive}
					loading={this.props.isUpdatingAnamnesis}
					handleButtonClicks={this.onHandleButtonClicks}
				/>
			</div>
		);
	}

	public render() {
		const { inputValue, inputError } = this.state;

		return (
			<div>
				<Spacer size="m" />

				<ControlledInput
					id="age"
					name="age"
					mobileFieldType={this.enableMobileDateKeyboard ? 'date' : undefined}
					placeholder={i18n('react.impulsCoach.components.anamnesis.determineAge.placeholderText')}
					value={inputValue}
					onChange={this.handleInputChange as any}
					onValidationDone={this.handleInputValidation}
					modifier="date"
					disabled={this.props.isUpdatingAnamnesis}
					autoComplete={!isNativePlatformAndroid()}
				/>
				<Spacer size="l" />
				{inputError === VALIDATE_IS_NOT_ALLOWED ? leaveCoach() : null}
				{!this.isRecommended() ? this.renderWarning() : null}
				{this.isValidSubmission() ? this.renderActiveButtons() : this.renderInactiveButtons()}
			</div>
		);
	}
}
